import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Users2, Trash2, Mail, MessageCircle, X, Search } from "lucide-react";
import LeadDetailDrawer from "@/components/leads/LeadDetailDrawer";
import ManualLeadDialog from "@/components/leads/ManualLeadDialog";
import { Link as RouterLink } from "react-router-dom";
import { BarChart3 } from "lucide-react";
import {
  DndContext, PointerSensor, KeyboardSensor, useSensor, useSensors,
  DragOverlay, closestCenter,
} from "@dnd-kit/core";
import LeadKanbanColumn from "@/components/leads/LeadKanbanColumn";
import LeadCard from "@/components/leads/LeadCard";

const STATUSES = [
  "new", "contacted", "interview_meeting", "send_info", "info_ready",
  "enrolled", "later", "not_interested",
  "introductory_class", "introductory_attended", "introductory_no_show",
];
const CAMPUSES = ["general", "santa_ana", "atenas", "grecia", "online"];
const SOURCES = ["wordpress_cf7", "whatsapp", "phone", "referral", "instagram", "manual"];

// 3 Kanban tabs. Each tab has its own set of columns and defines how a drop
// event should be interpreted (status change vs archived_column change).
const TABS = [
  {
    id: "pipeline",
    label_key: "lead_tab_pipeline",
    kind: "status",   // dropping updates `status`
    columns: [
      { id: "new" },
      { id: "contacted" },
      { id: "interview_meeting" },
      { id: "send_info" },
      { id: "info_ready" },
      { id: "enrolled" },
      { id: "later" },
      { id: "not_interested" },
    ],
  },
  {
    id: "introductory",
    label_key: "lead_tab_introductory",
    kind: "status",
    columns: [
      { id: "introductory_class" },
      { id: "introductory_attended" },
      { id: "introductory_no_show" },
    ],
  },
  {
    id: "archived",
    label_key: "lead_tab_archived",
    kind: "archived_column",  // dropping updates `archived_column`
    columns: [
      { id: "active" },
      { id: "inactive" },
      { id: "later" },
      { id: "not_interested" },
    ],
  },
];

export default function Leads() {
  const { t } = useI18n();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCampus, setFilterCampus] = useState("all");
  const [filterSource, setFilterSource] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [activeDragLead, setActiveDragLead] = useState(null);
  const [activeTab, setActiveTab] = useState("pipeline");

  const currentTab = TABS.find((tb) => tb.id === activeTab) || TABS[0];
  const columnIds = currentTab.columns.map((c) => c.id);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leads", { params: { tab: activeTab } });
      setLeads(data.items || []);
    } catch (e) {
      toast.error("Error loading leads");
    } finally {
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (filterCampus !== "all" && l.campus !== filterCampus) return false;
      if (filterSource !== "all") {
        // Legacy leads without `source` field are assumed to be wordpress_cf7.
        const s = l.source || "wordpress_cf7";
        if (s !== filterSource) return false;
      }
      if (!q) return true;
      return (
        (l.name || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.whatsapp || "").toLowerCase().includes(q) ||
        (l.group || "").toLowerCase().includes(q)
      );
    });
  }, [leads, filterCampus, filterSource, search]);

  const byColumn = useMemo(() => {
    const map = Object.fromEntries(columnIds.map((c) => [c, []]));
    for (const l of filtered) {
      // Pick the right key based on which tab we're on
      const key = currentTab.kind === "archived_column"
        ? (l.archived_column || "later")
        : l.status;
      if (map[key]) map[key].push(l);
    }
    return map;
  }, [filtered, columnIds, currentTab.kind]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = (event) => {
    const lead = leads.find((l) => l.id === event.active.id);
    if (lead) setActiveDragLead(lead);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDragLead(null);
    if (!over) return;
    const overId = over.id;
    let targetColumn = columnIds.includes(overId) ? overId : null;
    if (!targetColumn) {
      const overLead = leads.find((l) => l.id === overId);
      if (overLead) {
        targetColumn = currentTab.kind === "archived_column"
          ? (overLead.archived_column || "later")
          : overLead.status;
      }
    }
    if (!targetColumn) return;
    const dragged = leads.find((l) => l.id === active.id);
    if (!dragged) return;
    const currentColumn = currentTab.kind === "archived_column"
      ? (dragged.archived_column || "later")
      : dragged.status;
    if (currentColumn === targetColumn) return;

    // Build the correct PATCH payload for the tab kind
    const payload = currentTab.kind === "archived_column"
      ? { archived_column: targetColumn }
      : { status: targetColumn };

    setLeads((prev) => prev.map((l) => l.id === dragged.id ? { ...l, ...payload } : l));
    try {
      await api.patch(`/leads/${dragged.id}`, payload);
      const label = currentTab.kind === "archived_column"
        ? t(`lead_archived_${targetColumn}`)
        : t(`lead_status_${targetColumn}`);
      toast.success(t("lead_pipeline_moved").replace("{status}", label));
    } catch (e) {
      setLeads((prev) => prev.map((l) => l.id === dragged.id ? dragged : l));
      toast.error("Error updating status");
    }
  };

  const onLeadUpdated = (updated) => {
    setLeads((prev) => prev.map((l) => l.id === updated.id ? { ...l, ...updated } : l));
  };
  const onLeadDeleted = (id) => {
    setLeads((prev) => prev.filter((l) => l.id !== id));
    setSelectedLeadId(null);
  };

  return (
    <div className="space-y-6" data-testid="leads-page">
      <PageHeader
        title={t("leads_title")}
        subtitle={t("leads_subtitle")}
        icon={Users2}
        action={
          <RouterLink
            to="/dashboard/leads/analytics"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-slate-200 text-sm text-slate-700 hover:bg-slate-50 hover:text-brand-blue hover:border-brand-blue/40 transition-colors"
            data-testid="leads-view-analytics-btn"
          >
            <BarChart3 className="h-4 w-4" />
            {t("leads_view_analytics")}
          </RouterLink>
        }
      />

      {/* Analytics moved to /dashboard/leads/analytics for more Kanban space */}

      {/* Filters + Add Manual Lead */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder={t("lead_search_placeholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="leads-search-input"
          />
        </div>
        <Select value={filterCampus} onValueChange={setFilterCampus}>
          <SelectTrigger className="w-[180px]" data-testid="leads-campus-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("lead_filter_all_campuses")}</SelectItem>
            {CAMPUSES.map((c) => (
              <SelectItem key={c} value={c}>{t(`lead_campus_${c}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={setFilterSource}>
          <SelectTrigger className="w-[180px]" data-testid="leads-source-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("lead_filter_all_sources")}</SelectItem>
            {SOURCES.map((s) => (
              <SelectItem key={s} value={s}>{t(`lead_source_${s}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ManualLeadDialog onCreated={() => load()} />
        <div className="ml-auto text-sm text-slate-500" data-testid="leads-count">
          {t("lead_stats_total")}: <span className="font-semibold text-slate-900">{filtered.length}</span>
        </div>
      </div>

      {/* Tabs — 3 different Kanban flows */}
      <div className="border-b border-slate-200 flex gap-1 -mb-2" data-testid="leads-tabs">
        {TABS.map((tb) => {
          const isActive = tb.id === activeTab;
          return (
            <button
              key={tb.id}
              type="button"
              onClick={() => { setActiveTab(tb.id); setLoading(true); }}
              data-testid={`leads-tab-${tb.id}`}
              className={`px-4 py-2 text-sm font-medium rounded-t-md transition-colors border-b-2 -mb-px ${isActive
                ? "border-brand-blue text-brand-blue bg-white"
                : "border-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-50"}`}
            >
              {t(tb.label_key)}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />Loading…
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          {/* Con 7+ columnas el tablero se desliza de lado. Todas las columnas miden
              EXACTAMENTE lo mismo (ancho fijo y alto estirado), pase lo que pase con
              el largo del texto de las tarjetas. */}
          <div
            className={currentTab.columns.length >= 7
              ? "flex items-stretch gap-4 overflow-x-auto pb-3 -mx-1 px-1"
              : `grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${currentTab.columns.length >= 4 ? "xl:grid-cols-6" : "xl:grid-cols-4"}`}
          >
            {currentTab.columns.map((col) => {
              const titleKey = currentTab.kind === "archived_column"
                ? `lead_archived_${col.id}`
                : `lead_column_${col.id}`;
              const wide = currentTab.columns.length >= 7;
              return (
                <div
                  key={col.id}
                  className={wide
                    ? "shrink-0 grow basis-[248px] min-w-[248px]"
                    : ""}
                >
                  <LeadKanbanColumn
                    status={col.id}
                    title={t(titleKey)}
                    leads={byColumn[col.id] || []}
                    onLeadClick={setSelectedLeadId}
                  />
                </div>
              );
            })}
          </div>
          <DragOverlay>
            {activeDragLead && (
              <div className="opacity-90 rotate-2">
                <LeadCard lead={activeDragLead} isDragging />
              </div>
            )}
          </DragOverlay>
        </DndContext>
      )}

      <LeadDetailDrawer
        leadId={selectedLeadId}
        onClose={() => setSelectedLeadId(null)}
        onLeadUpdated={onLeadUpdated}
        onLeadDeleted={onLeadDeleted}
        onOpenLead={(id) => setSelectedLeadId(id)}
      />
    </div>
  );
}
